import { sortAscendingNumbers } from "../array.helpers";
import { MAX_COLOR_INDEX } from "./rides.map-colors.helpers";

const isValidPaletteRideId = (id: unknown): id is number =>
  typeof id === "number" && Number.isFinite(id) && id > 0;

/** Valid ride ids only, sorted ascending — stable palette order regardless of selection click timing. */
export const collectPaletteRideIds = (rideIds: readonly number[]): number[] => {
  const unique = new Set<number>();
  for (const id of rideIds) {
    if (isValidPaletteRideId(id)) {
      unique.add(id);
    }
  }
  return sortAscendingNumbers(unique);
};

/**
 * Next palette index for a ride. When all slots 1..MAX_COLOR_INDEX are taken, falls back to a
 * deterministic spread by `rideId` (may reuse colors — unavoidable beyond palette size).
 */
export const getNextAvailableColorId = (
  rideId: number,
  usedColorsIdsSet: Set<number>,
): number => {
  for (let i = 1; i <= MAX_COLOR_INDEX; i++) {
    if (!usedColorsIdsSet.has(i)) {
      return i;
    }
  }
  return (rideId % MAX_COLOR_INDEX) + 1;
};

const getStickyColorIdFromPrevMap = (
  rideId: number,
  prevMap: ReadonlyMap<number, number>,
): number | undefined => {
  if (!prevMap.has(rideId)) {
    return undefined;
  }
  const existingColor = prevMap.get(rideId);
  if (existingColor && existingColor >= 1 && existingColor <= MAX_COLOR_INDEX) {
    return existingColor;
  }
  return undefined;
};

export const buildRouteColorIdMatchesMap = ({
  rideIds,
  prevMap,
}: {
  rideIds: readonly number[];
  prevMap: ReadonlyMap<number, number>;
}): Map<number, number> => {
  const newMap = new Map<number, number>();
  const usedColorsIdsSet = new Set<number>();

  // Pass 1: rides that already had a color keep it (before new ids take free slots in sort order).
  for (const rideId of rideIds) {
    if (!isValidPaletteRideId(rideId)) {
      continue;
    }
    const stickyColorId = getStickyColorIdFromPrevMap(rideId, prevMap);
    if (
      stickyColorId &&
      !usedColorsIdsSet.has(stickyColorId) &&
      !newMap.has(rideId)
    ) {
      newMap.set(rideId, stickyColorId);
      usedColorsIdsSet.add(stickyColorId);
    }
  }

  // Pass 2: newly selected rides (or stale sticky conflicts) get the next free palette index.
  for (const rideId of rideIds) {
    if (!isValidPaletteRideId(rideId) || newMap.has(rideId)) {
      continue;
    }
    const nextColorId = getNextAvailableColorId(rideId, usedColorsIdsSet);
    usedColorsIdsSet.add(nextColorId);
    newMap.set(rideId, nextColorId);
  }

  return newMap;
};

export const areRouteColorIdMapsEqual = (
  left: ReadonlyMap<number, number>,
  right: ReadonlyMap<number, number>,
): boolean => {
  if (left.size !== right.size) {
    return false;
  }
  for (const [rideId, colorId] of left) {
    if (right.get(rideId) !== colorId) {
      return false;
    }
  }
  return true;
};

let stableRouteColorIdMatchesMapCache: Map<number, number> = new Map();

/** Returns the same `Map` reference when contents are unchanged — fewer Recoil subscriber updates. */
export const toStableRouteColorIdMatchesMap = (
  nextMap: Map<number, number>,
): Map<number, number> => {
  if (areRouteColorIdMapsEqual(stableRouteColorIdMatchesMapCache, nextMap)) {
    return stableRouteColorIdMatchesMapCache;
  }
  stableRouteColorIdMatchesMapCache = nextMap;
  return stableRouteColorIdMatchesMapCache;
};
